import { FormEvent, useState } from 'react';
import { Modal } from '@/components/ui/modal';
import { ApiError } from '@/lib/api';
import {
  requestMoreComplaintApi,
  resolveComplaintApi,
  reviewComplaintAppealApi,
  startComplaintApi,
  type ComplaintConclusion,
  type ComplaintDetail,
  type ComplaintRequestMoreRequest,
  type ComplaintResolveRequest,
  type ComplaintStartRequest,
} from '@/lib/complaints-api';
import {
  COMPLAINT_CONCLUSION_LABELS,
  complaintActionLabel,
  type ComplaintActionMode,
} from '@/lib/complaints-model';

interface Props {
  mode: ComplaintActionMode;
  detail: ComplaintDetail;
  onClose: () => void;
  onUpdated: (detail: ComplaintDetail) => void;
  onConflict: () => Promise<void>;
}

const INPUT =
  'w-full rounded-lg border border-slate-300 px-3 py-2 text-sm focus:border-brand-500 focus:outline-none focus:ring-2 focus:ring-brand-100';

/** 调用方必须用 mode + complaint id + version 作为 key 强制重挂载。 */
export function ComplaintActionModal({
  mode,
  detail,
  onClose,
  onUpdated,
  onConflict,
}: Props) {
  const [reason, setReason] = useState('');
  const [officialReply, setOfficialReply] = useState('');
  const [conclusion, setConclusion] =
    useState<ComplaintConclusion>('ESTABLISHED');
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const needsReason = mode === 'need-more';
  const needsResolution = mode === 'resolve' || mode === 'appeal-review';

  async function submit(event: FormEvent) {
    event.preventDefault();
    setError(null);
    if (needsReason && !reason.trim()) {
      setError('请填写要求补充的原因。');
      return;
    }
    if (needsResolution && !officialReply.trim()) {
      setError('请填写官方回复。');
      return;
    }

    setSubmitting(true);
    try {
      let next: ComplaintDetail;
      if (mode === 'start') {
        const input: ComplaintStartRequest = { version: detail.version };
        next = await startComplaintApi(detail.id, input);
      } else if (mode === 'need-more') {
        const input: ComplaintRequestMoreRequest = {
          reason: reason.trim(),
          version: detail.version,
        };
        next = await requestMoreComplaintApi(detail.id, input);
      } else {
        const input: ComplaintResolveRequest = {
          conclusion,
          officialReply: officialReply.trim(),
          version: detail.version,
        };
        next =
          mode === 'resolve'
            ? await resolveComplaintApi(detail.id, input)
            : await reviewComplaintAppealApi(detail.id, input);
      }
      onUpdated(next);
    } catch (caught) {
      if (caught instanceof ApiError && caught.status === 409) {
        await onConflict();
        return;
      }
      setError(toActionError(caught));
    } finally {
      setSubmitting(false);
    }
  }

  return (
    <Modal
      title={complaintActionLabel(mode)}
      description={`投诉单 ${detail.complaintNo} · 当前 version ${detail.version}`}
      onClose={onClose}
      footer={
        <>
          <button
            type="button"
            className="rounded-lg border border-slate-300 px-4 py-2 text-sm text-slate-700 hover:bg-slate-50"
            onClick={onClose}
            disabled={submitting}
          >
            取消
          </button>
          <button
            type="submit"
            form="complaint-action-form"
            className="rounded-lg bg-brand-500 px-4 py-2 text-sm font-medium text-white hover:bg-brand-600 disabled:opacity-60"
            disabled={submitting}
            data-testid="complaint-action-submit"
          >
            {submitting ? '提交中…' : '提交真实操作'}
          </button>
        </>
      }
    >
      <form id="complaint-action-form" className="space-y-4" onSubmit={submit}>
        {mode === 'start' ? (
          <p className="rounded-lg bg-amber-50 px-3 py-2 text-sm text-amber-800">
            领取后将由服务端执行 PENDING → IN_PROGRESS 状态机流转。
          </p>
        ) : null}

        {needsReason ? (
          <label className="block text-sm text-slate-700">
            <span className="mb-1 block">补充原因</span>
            <textarea
              className={`${INPUT} min-h-32`}
              value={reason}
              maxLength={2000}
              onChange={(event) => setReason(event.target.value)}
              data-testid="complaint-need-more-reason"
            />
          </label>
        ) : null}

        {needsResolution ? (
          <>
            <label className="block text-sm text-slate-700">
              <span className="mb-1 block">处理结论</span>
              <select
                className={INPUT}
                value={conclusion}
                onChange={(event) =>
                  setConclusion(event.target.value as ComplaintConclusion)
                }
              >
                {(Object.keys(COMPLAINT_CONCLUSION_LABELS) as ComplaintConclusion[]).map(
                  (value) => (
                    <option key={value} value={value}>
                      {COMPLAINT_CONCLUSION_LABELS[value]}
                    </option>
                  ),
                )}
              </select>
            </label>
            <label className="block text-sm text-slate-700">
              <span className="mb-1 block">官方回复</span>
              <textarea
                className={`${INPUT} min-h-40`}
                value={officialReply}
                maxLength={5000}
                onChange={(event) => setOfficialReply(event.target.value)}
                data-testid="complaint-official-reply"
              />
            </label>
          </>
        ) : null}

        {error ? (
          <div role="alert" className="rounded-lg bg-rose-50 px-3 py-2 text-sm text-rose-700">
            {error}
          </div>
        ) : null}
        <p className="text-xs text-slate-500">
          所有变更以服务端返回的状态和 version 为准；失败或冲突不会显示为成功。
        </p>
      </form>
    </Modal>
  );
}

function toActionError(error: unknown): string {
  if (error instanceof ApiError) {
    return error.status > 0
      ? `${error.message}（HTTP ${error.status}）`
      : error.message;
  }
  return error instanceof Error ? error.message : '操作失败，请稍后重试。';
}
